/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 */

/**
 * brandIcons.js — Favicon, apple-touch-icon e manifest PWA dinamici.
 *
 * Problema risolto: le icone statiche in /public hanno i colori di default; un clone white-label
 * con palette/logo propri mostrava un'icona diversa nella scheda del browser e in home screen.
 * Qui generiamo a runtime (canvas) un PNG dal marchio ufficiale — la STESSA geometria di
 * `public/icon.svg` e `LogoMark.jsx` — con i colori del brand, oppure usiamo il logo caricato,
 * e aggiorniamo <link rel=icon>, <link rel=apple-touch-icon> e il manifest.
 */

/** SVG del marchio ufficiale (identico a LogoMark.jsx / public/icon.svg), parametrico nei colori. */
export const brandMarkSvg = (primary, primaryAlt, accent = '#A7F3D0') => `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">
  <defs>
    <linearGradient id="grad" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="${primary}"/><stop offset="1" stop-color="${primaryAlt || primary}"/></linearGradient>
    <radialGradient id="shine" cx="30%" cy="28%" r="70%"><stop offset="0" stop-color="#FFFFFF" stop-opacity="0.35"/><stop offset="1" stop-color="#FFFFFF" stop-opacity="0"/></radialGradient>
  </defs>
  <rect x="2" y="2" width="60" height="60" rx="16" fill="url(#grad)"/><rect x="2" y="2" width="60" height="60" rx="16" fill="url(#shine)"/>
  <g fill="#FFFFFF" fill-opacity="0.97"><rect x="27.5" y="14" width="9" height="36" rx="3.5"/><rect x="14" y="27.5" width="36" height="9" rx="3.5"/></g>
  <g fill="${accent}"><circle cx="49" cy="14" r="2.4"/><circle cx="54.5" cy="20.5" r="1.7"/><circle cx="49" cy="27" r="1.7"/></g>
  <g stroke="${accent}" stroke-width="1.1" stroke-linecap="round" opacity="0.85"><line x1="49" y1="14" x2="54.5" y2="20.5"/><line x1="54.5" y1="20.5" x2="49" y2="27"/><line x1="49" y1="14" x2="49" y2="27"/></g>
  <path d="M8 55 L18 55 L22 50 L26 60 L32 46 L38 58 L44 55 L56 55" fill="none" stroke="#ECFDF5" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" opacity="0.65"/>
</svg>`;

const isSafeHex = (c) => /^#[0-9a-fA-F]{6}$/.test(String(c || ''));

/** Rasterizza una sorgente (SVG string o URL immagine) in PNG data-URL quadrato. */
const rasterize = (src, size) => new Promise((resolve, reject) => {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    try {
      const canvas = document.createElement('canvas');
      canvas.width = size; canvas.height = size;
      const ctx = canvas.getContext('2d');
      // Angoli arrotondati solo per i loghi caricati (l'SVG del marchio ha già il proprio bordo)
      if (!src.startsWith('data:image/svg+xml')) {
        const r = size * 0.25;
        ctx.beginPath();
        ctx.moveTo(r, 0); ctx.arcTo(size, 0, size, size, r); ctx.arcTo(size, size, 0, size, r); ctx.arcTo(0, size, 0, 0, r); ctx.arcTo(0, 0, size, 0, r); ctx.closePath();
        ctx.clip();
      }
      // cover: ritaglia centrato mantenendo le proporzioni
      const s = Math.max(size / img.naturalWidth, size / img.naturalHeight);
      const w = img.naturalWidth * s; const h = img.naturalHeight * s;
      ctx.drawImage(img, (size - w) / 2, (size - h) / 2, w, h);
      resolve(canvas.toDataURL('image/png'));
    } catch (e) { reject(e); }
  };
  img.onerror = reject;
  img.src = src;
});

const setLink = (rel, href, extra = {}) => {
  let link = document.querySelector(`link[rel='${rel}']`);
  if (!link) { link = document.createElement('link'); link.rel = rel; document.head.appendChild(link); }
  Object.entries(extra).forEach(([k, v]) => link.setAttribute(k, v));
  link.href = href;
};

let _lastKey = null;
let _manifestBlobUrl = null;

/**
 * Applica icone + manifest coerenti con il brand. Idempotente (ricalcola solo se cambia qualcosa).
 * Con brand di default (colori standard, nessun logo) lascia i file statici: zero lavoro extra.
 */
export const applyBrandIcons = async (brand, { isDefault = false } = {}) => {
  if (typeof document === 'undefined') return;
  const primary = isSafeHex(brand.primaryColor) ? brand.primaryColor : '#0F766E';
  const primaryAlt = isSafeHex(brand.primaryColorAlt) ? brand.primaryColorAlt : (isSafeHex(brand.secondaryColor) ? brand.secondaryColor : '#059669');
  const accent = isSafeHex(brand.accentColor) ? brand.accentColor : '#A7F3D0';
  const source = brand.faviconUrl || brand.logoUrl || null;
  const key = JSON.stringify([primary, primaryAlt, accent, source ? source.slice(0, 64) + source.length : null, brand.clinicName]);
  if (key === _lastKey) return;
  _lastKey = key;
  if (isDefault && !source) return; // icone statiche già perfette

  try {
    const src = source || `data:image/svg+xml;charset=utf-8,${encodeURIComponent(brandMarkSvg(primary, primaryAlt, accent))}`;
    const [i192, i512, i180, i64] = await Promise.all([rasterize(src, 192), rasterize(src, 512), rasterize(src, 180), rasterize(src, 64)]);
    setLink('icon', i64, { type: 'image/png', sizes: '64x64' });
    setLink('apple-touch-icon', i180, { sizes: '180x180' });

    // Manifest dinamico: nome, colori e icone del brand (start_url/scope assoluti: necessario con blob:)
    const res = await fetch('/manifest.json', { cache: 'force-cache' });
    const base = res.ok ? await res.json() : {};
    const manifest = {
      ...base,
      name: brand.clinicName || base.name,
      short_name: brand.clinicShortName || brand.clinicName || base.short_name,
      theme_color: primary,
      start_url: `${window.location.origin}/`,
      scope: `${window.location.origin}/`,
      icons: [
        { src: i192, sizes: '192x192', type: 'image/png', purpose: 'any maskable' },
        { src: i512, sizes: '512x512', type: 'image/png', purpose: 'any maskable' },
      ],
    };
    if (_manifestBlobUrl) URL.revokeObjectURL(_manifestBlobUrl);
    _manifestBlobUrl = URL.createObjectURL(new Blob([JSON.stringify(manifest)], { type: 'application/manifest+json' }));
    setLink('manifest', _manifestBlobUrl);
    const theme = document.querySelector("meta[name='theme-color']");
    if (theme) theme.setAttribute('content', primary);
  } catch (e) {
    // Mai bloccare l'app per un'icona: restano i file statici.
    if (typeof console !== 'undefined') console.warn('[brandIcons] generazione icone saltata:', e?.message || e);
  }
};
